import type { FC, MouseEvent } from 'react';
import { useRouter } from 'next/router';
import { getUrlPathFromSlug } from '@utils/common';
import { APP_BASE_PATH } from '@constants/app';

type onClick = (e: MouseEvent<HTMLElement>) => void;
type SimpleLinkProps = { href: string; onClick?: onClick; children?: React.ReactNode };

const basePath = `/${APP_BASE_PATH}`;
const apiLinksListRoute = ['api-ref/enums', 'api-ref/callbacks', 'api-ref/functions', 'api-ref/structs'];
const isAbsoluteHref = (href: string): boolean => href.startsWith('http');
const isMailHref = (href: string): boolean => href.startsWith('mailto:');
const isAnchor = (href: string): boolean => href.startsWith('#');

/**
 * Generate href without locale (clean URLs)
 * Middleware will handle adding locale internally
 */
const getHrefLink = (href: string, basePath: string) => {
  switch (true) {
    case isAbsoluteHref(href):
    case isMailHref(href):
    case isAnchor(href):
      return href;

    default:
      // Clean URLs without locale: /docs/page instead of /docs/ko/page
      return `${basePath}${href}`;
  }
};

export const SimpleLink: FC<SimpleLinkProps> = ({ href, onClick, children }) => {
  const router = useRouter();
  const pathFromSlug = getUrlPathFromSlug(router.query.slug);
  const includesPath = apiLinksListRoute.includes(pathFromSlug);
  const hrefLink = getHrefLink(href, basePath);

  const nativeOnClick: onClick = (e) => {
    if (!isAbsoluteHref(href) && !isMailHref(href)) {
      e.preventDefault();
      // Middleware reads locale from the EPIC_LOCALE cookie
      router.push(href);
    }
  };

  return (
    <a href={hrefLink} onClick={onClick || nativeOnClick} className={includesPath ? 'api-link' : undefined}>
      {children}
    </a>
  );
};
